import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { finalize } from 'rxjs';

import { EnterpriseSubscriptionService } from '../enterprise-subscription/enterprise-subscription.service';
import {
    TrialOnboardingChecklistItem,
    TrialOnboardingWorkspaceResponse,
    TrialOnboardingWorkspaceTemplate
} from '../enterprise-subscription/enterprise-subscription.model';
import { SessionStorageService } from '../services/session-storage.service';

@Component({
    selector: 'app-first-run-workspace',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        ButtonModule,
        ProgressBarModule,
        TagModule,
        ToastModule
    ],
    providers: [MessageService],
    templateUrl: './first-run-workspace.component.html',
    styleUrls: ['./first-run-workspace.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FirstRunWorkspaceComponent implements OnInit {

    workspace = signal<TrialOnboardingWorkspaceResponse | null>(null);
    loading = signal(false);
    updatingItemKey = signal<string | null>(null);

    completionPercent = computed(() => {
        const current = this.workspace();
        if (!current?.totalCount) {
            return 0;
        }
        return Math.round((current.completedCount / current.totalCount) * 100);
    });

    checklist = computed(() => this.workspace()?.checklist ?? []);
    templates = computed(() => this.workspace()?.templates ?? []);
    provisioning = computed(() => this.workspace()?.provisioning ?? null);

    private readonly enterpriseSubscriptionService = inject(EnterpriseSubscriptionService);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly messageService = inject(MessageService);
    private readonly router = inject(Router);

    ngOnInit(): void {
        this.loadWorkspace();
    }

    openRoute(route: string | undefined): void {
        if (!route) {
            return;
        }
        this.router.navigate([route]);
    }

    markDone(item: TrialOnboardingChecklistItem): void {
        this.updateChecklistItem(item, 'COMPLETED', 'USER_MARKED');
    }

    skipItem(item: TrialOnboardingChecklistItem): void {
        this.updateChecklistItem(item, 'SKIPPED', 'USER_SKIPPED');
    }

    updateChecklistItem(item: TrialOnboardingChecklistItem, status: 'COMPLETED' | 'SKIPPED', completionSource: string): void {
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId || !item?.itemKey) {
            return;
        }
        this.updatingItemKey.set(item.itemKey);
        this.enterpriseSubscriptionService.markTrialOnboardingChecklistItem(companyId, item.itemKey, {
            status,
            completionSource
        }).pipe(
            finalize(() => this.updatingItemKey.set(null))
        ).subscribe({
            next: response => {
                if (!response.success || !response.data) {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Checklist not updated',
                        detail: response.message || 'Please try again.'
                    });
                    return;
                }
                this.patchChecklistItem(response.data);
            },
            error: error => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Checklist not updated',
                    detail: error?.error?.message || error?.message || 'Please try again.'
                });
            }
        });
    }

    isCompleted(item: TrialOnboardingChecklistItem): boolean {
        return item.status === 'COMPLETED';
    }

    isClosed(item: TrialOnboardingChecklistItem): boolean {
        return item.status === 'COMPLETED' || item.status === 'SKIPPED';
    }

    isUpdating(item: TrialOnboardingChecklistItem): boolean {
        return this.updatingItemKey() === item.itemKey;
    }

    itemIcon(item: TrialOnboardingChecklistItem): string {
        if (this.isCompleted(item)) {
            return 'pi pi-check';
        }
        if (item.status === 'SKIPPED') {
            return 'pi pi-minus';
        }
        return switchIcon(item.itemKey);
    }

    private loadWorkspace(): void {
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) {
            this.router.navigate(['/enterprise-subscription']);
            return;
        }
        this.loading.set(true);
        this.enterpriseSubscriptionService.getTrialOnboardingWorkspace(companyId).pipe(
            finalize(() => this.loading.set(false))
        ).subscribe({
            next: response => {
                if (!response.success || !response.data) {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Workspace unavailable',
                        detail: response.message || 'Could not load onboarding.'
                    });
                    return;
                }
                this.workspace.set(response.data);
            },
            error: error => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Workspace unavailable',
                    detail: error?.error?.message || error?.message || 'Could not load onboarding.'
                });
            }
        });
    }

    private patchChecklistItem(item: TrialOnboardingChecklistItem): void {
        this.workspace.update(current => {
            if (!current) {
                return current;
            }
            const checklist = current.checklist.map(existing => existing.itemKey === item.itemKey ? item : existing);
            const completedCount = checklist.filter(existing => existing.status === 'COMPLETED').length;
            return {
                ...current,
                checklist,
                completedCount
            };
        });
    }
}

function switchIcon(itemKey: string): string {
    switch (itemKey) {
        case 'SET_COMPANY_BASICS':
            return 'pi pi-building';
        case 'ADD_FIRST_CUSTOMER':
            return 'pi pi-briefcase';
        case 'CREATE_FIRST_SHIPMENT':
            return 'pi pi-truck';
        case 'INVITE_TEAMMATE':
            return 'pi pi-users';
        case 'CREATE_FIRST_INVOICE':
            return 'pi pi-file';
        default:
            return 'pi pi-circle';
    }
}
